/** Ngữ cảnh trang hiện tại cho trợ lý AI: bước BSC + gợi ý câu hỏi theo bước. */
import type { AiHubPageContext } from '@/types/ai-agent';

interface StepInfo {
  step?: string;
  label: string;
  suggestions: string[];
}

const STEP_BY_PATH: Record<string, StepInfo> = {
  '/danh-gia': {
    step: 'b1',
    label: 'B1 · Đánh giá',
    suggestions: [
      'Gợi ý yếu tố thành công then chốt của ngành tôi',
      'Phân tích điểm mạnh so với đối thủ chính',
      'Lợi thế cạnh tranh nào bền vững theo VRIO?',
      'Thêm điểm mạnh công ty «…»',
    ],
  },
  '/xay-dung-chien-luoc/swot': {
    step: 'b2',
    label: 'B2 · Phân tích SWOT',
    suggestions: ['Gợi ý cơ hội và thách thức thị trường', 'Điểm yếu nào cần ưu tiên khắc phục?', 'Thêm cơ hội «…» vào SWOT'],
  },
  '/xay-dung-chien-luoc/chien-luoc': {
    step: 'b2',
    label: 'B2 · Xây dựng chiến lược',
    suggestions: ['Đề xuất chiến lược SO/WO/ST/WT từ SWOT', 'Chiến lược nào phù hợp nguồn lực hiện tại?', 'Thêm chiến lược SO dựa trên điểm mạnh và cơ hội đầu tiên'],
  },
  '/ket-qua-chien-luoc/lua-chon': {
    step: 'b3',
    label: 'B3 · Lựa chọn chiến lược',
    suggestions: ['Tiêu chí nào để chọn chiến lược ưu tiên?', 'So sánh các chiến lược đang có', 'Chọn chiến lược «…»'],
  },
  '/ket-qua-chien-luoc/ket-qua': {
    step: 'b3',
    label: 'B3 · Kết quả chiến lược',
    suggestions: ['Viết kết quả kỳ vọng cho chiến lược đã chọn'],
  },
  '/ban-do-chien-luoc-thanh-phan': {
    step: 'b4',
    label: 'B4 · Bản đồ chiến lược thành phần',
    suggestions: ['Mục tiêu nào nên có ở 4 phương diện BSC?', 'Kiểm tra quan hệ nhân quả trên bản đồ', 'Thêm mục tiêu «…» vào phương diện Khách hàng'],
  },
  '/ban-do-chien-luoc': {
    step: 'b4',
    label: 'B4 · Bản đồ chiến lược công ty',
    suggestions: ['Đề xuất mục tiêu công ty theo 4 phương diện', 'Thêm mục tiêu công ty «…» ở phương diện Tài chính'],
  },
  '/buoc5': {
    step: 'b5',
    label: 'B5 · Mô hình xương cá',
    suggestions: [
      'Gợi ý yếu tố thành công then chốt (xương cá) cho mục tiêu tăng doanh thu',
      'Gợi ý KPI cho từng mục tiêu',
      'Nguyên nhân gốc của mục tiêu chưa đạt là gì?',
      'Thêm KPI «…» cho xương cá đầu tiên',
    ],
  },
  '/buoc6': {
    step: 'b6',
    label: 'B6 · Đo lường chỉ số',
    suggestions: ['KPI nào chưa có chỉ tiêu hoặc tỉ trọng?', 'Gợi ý chỉ tiêu cho các KPI dựa trên kết quả kỳ trước'],
  },
  '/buoc7': {
    step: 'b7',
    label: 'B7 · Phân bổ tỉ trọng',
    suggestions: ['Tỉ trọng hiện tại của bộ phận có hợp lý không?', 'Gợi ý phân bổ tỉ trọng theo ưu tiên chiến lược'],
  },
  '/dashboard': {
    label: 'Hiệu suất KPI',
    suggestions: ['KPI nào đang đỏ kỳ này?', 'Mục tiêu nào hoàn thành thấp nhất, vì sao?', 'Tóm tắt hiệu suất của bộ phận kỳ này'],
  },
  '/bao-cao-kpis': {
    label: 'Báo cáo KPI',
    suggestions: ['KPI nào chưa báo cáo kỳ này?', 'Bộ phận nào đang chậm báo cáo?'],
  },
  '/thong-ke-bao-cao-kpis': {
    label: 'Thống kê báo cáo KPI',
    suggestions: ['KPI nào chưa báo cáo kỳ này?', 'Tóm tắt tình hình báo cáo theo bộ phận'],
  },
  '/bao-cao-bsc': {
    label: 'Báo cáo BSC',
    suggestions: ['Tóm tắt kết quả BSC kỳ này', 'KPI nào đang đỏ kỳ này?'],
  },
  '/bang-ke-hoach': {
    label: 'Bảng kế hoạch',
    suggestions: ['Hạng mục nào đang trễ tiến độ?', 'Tóm tắt tiến độ kế hoạch của bộ phận'],
  },
  '/hop-tuan': {
    label: 'Họp tuần',
    suggestions: ['Chuẩn bị nội dung họp tuần', 'Tóm tắt tình hình tuần này của bộ phận'],
  },
  '/giai-doan': {
    label: 'Giai đoạn chiến lược',
    suggestions: ['Giai đoạn hiện tại kéo dài tới khi nào?', 'Mỗi giai đoạn nên tập trung mục tiêu gì?'],
  },
  '/action-plan-v2': {
    label: 'Action Plan V2',
    suggestions: [
      'Việc nào của tôi đang quá hạn?',
      'Tạo việc mới gắn mục tiêu phụ trách',
    ],
  },
};

export const DEFAULT_AI_SUGGESTIONS = [
  'Giải thích Balanced Scorecard trong 5 ý',
  'Tôi nên bắt đầu xây dựng chiến lược từ đâu?',
  'Tóm tắt kế hoạch hiện tại của công ty',
];

/** Gợi ý chung mọi trang: trợ lý tra Hướng dẫn sử dụng (engine `search_guide`) theo trang đang mở, kèm link bài. */
export const GUIDE_SUGGESTION = 'Hướng dẫn thao tác trang này';

export const getAiPageContext = (pathname: string): AiHubPageContext & { suggestions: string[] } => {
  const clean = pathname.replace(/\/$/, '') || '/';
  // Khớp đúng path hoặc trang con (/buoc5/…, route động) — ưu tiên prefix dài nhất
  const key = Object.keys(STEP_BY_PATH)
    .filter((p) => clean === p || clean.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];
  const info = key ? STEP_BY_PATH[key] : undefined;
  if (!info) return { route: clean, suggestions: [...DEFAULT_AI_SUGGESTIONS, GUIDE_SUGGESTION] };
  return { route: clean, step: info.step, label: info.label, suggestions: [...info.suggestions, GUIDE_SUGGESTION] };
};
